import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowUpRight } from 'lucide-react';
import { hero, SITE } from '../data/mock';
import HeroMusic from './HeroMusic';
import HeroVideo from './HeroVideo';
import HeroStats from './HeroStats';
import { isVideoUrl } from '../utils/mediaType';
import { fitToClass } from '../utils/mediaFit';
import { useCopy } from '../context/SiteCopyContext';
import { useSiteMedia } from '../utils/useSiteMedia';

/**
 * AdminHero — admin-managed hero slideshow.
 *
 * Reads slot `hero` from /api/site-media and renders curated media (images +
 * videos) with the chosen transition. Falls back to the built-in <HeroVideo>
 * when no media has been uploaded yet.
 */
const AdminHero = () => {
  const cfg = useSiteMedia('hero');
  const [active, setActive] = useState(0);
  const timerRef = useRef(null);
  const videoRef = useRef(null);

  const ctaPrimary = useCopy('home.hero.cta_primary', 'Shop the collection');
  const ctaSecondary = useCopy('home.hero.cta_secondary', 'Our story');

  const media = cfg?.media || [];
  const transition = cfg?.transition || 'fade';
  const slideMs = cfg?.duration_ms || 5000;
  const transitionMs = cfg?.transition_ms || 900;
  const total = media.length;

  useEffect(() => {
    if (total <= 1) return undefined;
    const current = media[active];
    if (current?.kind === 'video' || (current && isVideoUrl(current.url))) return undefined;
    timerRef.current = window.setTimeout(() => setActive((i) => (i + 1) % total), slideMs);
    return () => window.clearTimeout(timerRef.current);
  }, [active, total, slideMs, media]);

  if (cfg === null) return null;
  if (total === 0) return <HeroVideo />;
  const titleWords = (hero?.titleItalic || 'thread').toString();
  const before = (hero?.titleBefore || 'Where timber meets').toString();
  const trailing = (hero?.titleAfter || '.').toString();

  return (
    <section className="relative w-full h-[100svh] min-h-[640px] max-h-[1000px] overflow-hidden bg-[#0F0A06]" data-testid="admin-hero">
      {media.map((m, i) => {
        const isActive = i === active;
        const next = (active + 1) % total;
        const isNext = i === next;
        if (!isActive && !isNext && total > 2) return null;
        const isVid = m.kind === 'video' || isVideoUrl(m.url);
        let stateClass = '';
        let styleExtra = { transition: `opacity ${transitionMs}ms ease, transform ${transitionMs + 400}ms ease` };
        if (transition === 'fade' || transition === 'dissolve') {
          stateClass = isActive ? 'opacity-100' : 'opacity-0';
        } else if (transition === 'slide') {
          stateClass = isActive ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12';
          styleExtra = { transition: `opacity ${transitionMs}ms ease, transform ${transitionMs}ms ease` };
        } else if (transition === 'zoom') {
          stateClass = isActive ? 'opacity-100 scale-[1.08]' : 'opacity-0 scale-100';
          styleExtra = { transition: `opacity ${transitionMs}ms ease, transform ${slideMs}ms ease-out` };
        }
        return (
          <div key={`${m.url}-${i}`} className={`absolute inset-0 ${stateClass}`} style={styleExtra}>
            {isVid ? (
              <video
                ref={isActive ? videoRef : undefined}
                src={m.url}
                autoPlay={isActive}
                muted
                loop={total === 1}
                playsInline
                onEnded={() => { if (total > 1 && isActive) setActive((j) => (j + 1) % total); }}
                className={`w-full h-full ${fitToClass(m.fit)}`}
              />
            ) : (
              <img src={m.url} alt={m.alt || ''} className={`w-full h-full ${fitToClass(m.fit)}`} />
            )}
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/55" />
          </div>
        );
      })}

      {total > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20" data-testid="hero-dots">
          {media.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              data-testid={`hero-dot-${i}`}
              aria-label={`Slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${i === active ? 'w-9 bg-[#F5EDDD]' : 'w-1.5 bg-[#F5EDDD]/40 hover:bg-[#F5EDDD]/70'}`}
            />
          ))}
        </div>
      )}

      {/* Right-edge vertical marquee — labels scroll upward continuously */}
      <div className="absolute right-3 lg:right-10 inset-y-0 z-10 pointer-events-none w-[20px] lg:w-[24px] overflow-hidden">
        <div className="hero-vertical-marquee absolute inset-x-0 top-0 flex flex-col items-end gap-12">
          {[0, 1].map((rep) => (
            <React.Fragment key={rep}>
              {['Solid timber', 'Heavyweight bouclé', 'Made in Melbourne', 'Designed in Australia', 'Built to outlast trends'].map((t, i) => (
                <span
                  key={`${rep}-${i}`}
                  style={{ writingMode: 'vertical-rl' }}
                  className="text-[9px] lg:text-[10px] tracking-[0.4em] lg:tracking-[0.5em] uppercase text-[#F5EDDD]/60 whitespace-nowrap"
                >
                  {t}
                </span>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Compact bottom-left copy block — never covers the video */}
      <div className="relative z-10 h-full max-w-[1480px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-10 md:pb-16">
        <div className="max-w-[330px] sm:max-w-lg md:max-w-2xl pr-2 lg:pr-0">
          <h1
            className="text-[#F5EDDD] text-[42px] leading-[1.0] font-normal sm:text-5xl sm:font-light md:text-[64px] lg:text-[72px] tracking-[-0.02em] [text-shadow:0_2px_24px_rgba(0,0,0,0.4)] animate-[fadeUp_1100ms_ease-out_120ms_both]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            <span>{before} </span>
            <span className="italic" style={{ color: '#E3D5B8' }}>{titleWords}</span>
            <span>{trailing}</span>
          </h1>
          <p className="mt-3 md:mt-4 text-[9px] md:text-[10px] tracking-[0.38em] md:tracking-[0.42em] uppercase text-[#E3D5B8] animate-[fadeUp_1100ms_ease-out_220ms_both]">
            {SITE?.tagline || hero?.eyebrow || 'Made in Australia · Limited release'}
          </p>
          <div className="mt-5 md:mt-6 flex flex-wrap items-center gap-3 md:gap-4 animate-[fadeUp_1100ms_ease-out_420ms_both]">
            <Button asChild className="bg-[#F5EDDD] text-[#33200F] hover:bg-[#FBF6EC] rounded-none h-11 md:h-12 px-6 md:px-9 text-[11px] md:text-[12px] tracking-[0.18em] md:tracking-[0.22em] uppercase">
              <Link to="/collections">{ctaPrimary}</Link>
            </Button>
            <Button asChild variant="outline" className="bg-transparent border border-[#F5EDDD]/70 text-[#F5EDDD] hover:bg-[#F5EDDD] hover:text-[#33200F] rounded-none h-11 md:h-12 px-5 md:px-7 text-[11px] md:text-[12px] tracking-[0.18em] md:tracking-[0.22em] uppercase">
              <Link to="/about">{ctaSecondary} <ArrowUpRight className="ml-2 h-3.5 w-3.5" /></Link>
            </Button>
          </div>
          {/* Stats — desktop only (hidden on mobile per request) */}
          <div className="hidden lg:block">
            <HeroStats tone="dark" />
          </div>
        </div>
      </div>

      <HeroMusic />
    </section>
  );
};

export default AdminHero;
